import {
  create,
  type InternalAxiosRequestConfig,
  type AxiosResponse,
} from 'axios';
import type { Geometry, Polygon, Position } from 'geojson';
import type { AreaType } from '../../constants/areaType';
import type { LinkType } from '../../constants/linkType';
import type {
  SimDataMeta,
  Area,
  Link,
  Trip,
  Agent,
  FeatureOptions,
  AreaAggregation,
  LinkAggregation,
  CommonAggregation,
  CrossAggregation,
  HistogramData,
  SavedPolygons,
  PolygonType,
  OdMapResult,
} from '../../types/domain';
import { DATA_API_BASE_URL } from './config';

type AreaAggregationType =
  | 'home_traveled_distance_avg'
  | 'work_traveled_distance_avg'
  | 'start_traveled_distance_avg'
  | 'end_traveled_distance_avg'
  | 'home_traveled_time_avg'
  | 'work_traveled_time_avg'
  | 'start_traveled_time_avg'
  | 'end_traveled_time_avg'
  | 'home_activity_time_avg'
  | 'work_activity_time_avg'
  | 'start_activity_time_avg'
  | 'end_activity_time_avg'
  | 'home_agents_per_unit_area'
  | 'home_pref_agents_per_unit_area'
  | 'work_agents_per_unit_area'
  | 'start_trips_per_unit_area'
  | 'end_trips_per_unit_area';

type LinkAggregationType = 'passed_routes_count';

type AgentAggregationType = 'gender' | 'age' | 'nationality';

type TripAggregationType = 'end_activity_type' | 'longest_distance_mode';

type AttributeAggregationType = AgentAggregationType | TripAggregationType;

type TripHistogramType =
  | 'traveled_time'
  | 'traveled_distance'
  | 'activity_time'
  | 'dep_time'
  | 'arr_time'
  | 'passage_time'
  | 'staying_population';

type Bucket =
  | { type: 'BucketCount'; bucketCount: number }
  | { type: 'BucketWidth'; bucketWidth: number };

type OdMapType = 'mesh_125m' | 'mesh_1km' | 'town' | 'city';

type RawGeometry<T extends { geometry: Geometry }> = Omit<T, 'geometry'> & {
  geometry: string;
};

type RawValue<T extends { value: unknown }> = Omit<T, 'value'> & {
  value: string;
};

type ApiDataFilter = {
  start?: {
    area?: string[];
    polygon?: [number, number][][];
  };
  end?: {
    area?: string[];
    polygon?: [number, number][][];
  };
  pass?: {
    area?: string[];
    link?: string[];
    polygon?: [number, number][][];
    relation?: 'and' | 'or';
  };
  home?: {
    area?: string[];
    polygon?: [number, number][][];
  };
  work?: {
    area?: string[];
    polygon?: [number, number][][];
  };
  visit?: {
    area?: string[];
    polygon?: [number, number][][];
  };
  end_activity_type?: string[];
  longest_distance_mode?: string[];
  traveled_time?: [number, number];
  traveled_distance?: [number, number];
  activity_time?: [number, number];
  dep_time?: [number, number];
  arr_time?: [number, number];
  passage_time?: [number, number];
  age?: number[];
  gender?: number[];
  nationality?: string[];
  staying_time?: [number, number];
  stopBy?: 'before' | 'after' | 'all';
};

const axiosInstance = create({
  baseURL: DATA_API_BASE_URL,
});

let accessTokenProvider: (() => string | Promise<string>) | null = null;

axiosInstance.interceptors.request.use(
  async (
    config: InternalAxiosRequestConfig
  ): Promise<InternalAxiosRequestConfig> => {
    if (!accessTokenProvider) {
      throw new Error('API client is not initialized.');
    }
    const accessToken = await accessTokenProvider();
    config.headers['Authorization'] = `Bearer ${accessToken}`;
    return config;
  }
);

const initializeDataApiClient = (
  provider: () => string | Promise<string>
): void => {
  accessTokenProvider = provider;
};

const dataApiClient = {
  getSimDataMetaList: async (): Promise<SimDataMeta[]> => {
    return axiosInstance
      .get('/simdata')
      .then(
        (response: AxiosResponse<{ items: SimDataMeta[] }>): SimDataMeta[] => {
          return response.data.items;
        }
      );
  },
  getGeometry: async (simdataId: string): Promise<Polygon> => {
    return axiosInstance
      .get(`/simdata/${simdataId}/geometry`)
      .then(
        (
          response: AxiosResponse<{ reference_area_geometry: string }>
        ): Polygon => {
          return JSON.parse(response.data.reference_area_geometry);
        }
      );
  },
  getAreas: async (simdataId: string): Promise<Area[]> => {
    return axiosInstance
      .get(`/simdata/${simdataId}/areas`)
      .then(
        (response: AxiosResponse<{ items: RawGeometry<Area>[] }>): Area[] => {
          return response.data.items.map((item: RawGeometry<Area>): Area => {
            return {
              ...item,
              geometry: JSON.parse(item.geometry),
            };
          });
        }
      );
  },
  getAreaCity: async (simdataId: string): Promise<Area[]> => {
    return axiosInstance
      .get(`/simdata/${simdataId}/area_city`)
      .then(
        (response: AxiosResponse<{ items: RawGeometry<Area>[] }>): Area[] => {
          return response.data.items.map((item: RawGeometry<Area>): Area => {
            return {
              ...item,
              geometry: JSON.parse(item.geometry),
            };
          });
        }
      );
  },
  getAreaPref: async (simdataId: string): Promise<Area[]> => {
    return axiosInstance
      .get(`/simdata/${simdataId}/area_pref`)
      .then(
        (response: AxiosResponse<{ items: RawGeometry<Area>[] }>): Area[] => {
          return response.data.items.map((item: RawGeometry<Area>): Area => {
            return {
              ...item,
              geometry: JSON.parse(item.geometry),
            };
          });
        }
      );
  },
  getLinks: async (simdataId: string): Promise<Link[]> => {
    return axiosInstance
      .get(`/simdata/${simdataId}/links`)
      .then(
        (response: AxiosResponse<{ items: RawGeometry<Link>[] }>): Link[] => {
          return response.data.items.map((item: RawGeometry<Link>): Link => {
            return {
              ...item,
              geometry: JSON.parse(item.geometry),
            };
          });
        }
      );
  },
  getSummary: async (
    simdataId: string,
    apiFilter: ApiDataFilter,
    signal?: AbortSignal
  ): Promise<number> => {
    return axiosInstance
      .post(
        `/simdata/${simdataId}/summary`,
        { conditions: apiFilter },
        { signal }
      )
      .then((response: AxiosResponse<{ agent_count: number }>): number => {
        return response.data.agent_count;
      });
  },
  getTrips: async (
    simdataId: string,
    apiFilter: ApiDataFilter,
    details: boolean,
    signal?: AbortSignal
  ): Promise<Trip[]> => {
    return axiosInstance
      .post(
        `/simdata/${simdataId}/trips`,
        {
          conditions: apiFilter,
          details: details,
        },
        { signal }
      )
      .then((response: AxiosResponse<{ items: Trip[] }>): Trip[] => {
        // APIからのレスポンスのサイズが大きすぎると、response.dataが空文字になることがあるため検証する
        if (!response.data || typeof response.data !== 'object') {
          throw new Error(
            'Failed to fetch trips data: response data is empty or too large.'
          );
        }
        return response.data.items;
      });
  },
  getFeatureOptions: async (simdataId: string): Promise<FeatureOptions> => {
    return axiosInstance
      .get(`/simdata/${simdataId}/feature-options`)
      .then((response: AxiosResponse<FeatureOptions>): FeatureOptions => {
        return response.data;
      });
  },
  getAgents: async (
    simdataId: string,
    apiFilter: ApiDataFilter,
    signal?: AbortSignal
  ): Promise<Agent[]> => {
    return axiosInstance
      .post(
        `/simdata/${simdataId}/agents`,
        { conditions: apiFilter },
        { signal }
      )
      .then((response: AxiosResponse<{ items: Agent[] }>): Agent[] => {
        return response.data.items;
      });
  },
  getAreaAggr: async (
    simdataId: string,
    apiFilter: ApiDataFilter,
    aggregationType: AreaAggregationType,
    areaType: AreaType | null,
    signal?: AbortSignal
  ): Promise<AreaAggregation[]> => {
    return axiosInstance
      .post(
        `/simdata/${simdataId}/area-aggr/${aggregationType}`,
        {
          area_type: areaType,
          conditions: apiFilter,
        },
        { signal }
      )
      .then(
        (
          response: AxiosResponse<{ items: RawValue<AreaAggregation>[] }>
        ): AreaAggregation[] => {
          return response.data.items.map(
            (item: RawValue<AreaAggregation>): AreaAggregation => {
              return {
                ...item,
                value: Number(item.value),
              };
            }
          );
        }
      );
  },
  getLinkAggr: async (
    simdataId: string,
    apiFilter: ApiDataFilter,
    aggregationType: LinkAggregationType,
    linkTypes: LinkType[],
    signal?: AbortSignal
  ): Promise<LinkAggregation[]> => {
    return axiosInstance
      .post(
        `/simdata/${simdataId}/link-aggr/${aggregationType}`,
        {
          conditions: apiFilter,
          link_types: linkTypes,
        },
        { signal }
      )
      .then(
        (
          response: AxiosResponse<{ items: RawValue<LinkAggregation>[] }>
        ): LinkAggregation[] => {
          return response.data.items.map(
            (item: RawValue<LinkAggregation>): LinkAggregation => {
              return {
                ...item,
                value: Number(item.value),
              };
            }
          );
        }
      );
  },
  getAgentCrossAggr: async (
    simdataId: string,
    apiFilter: ApiDataFilter,
    signal?: AbortSignal
  ): Promise<CrossAggregation[]> => {
    return axiosInstance
      .post(
        `/simdata/${simdataId}/agent-cross-aggr`,
        {
          conditions: apiFilter,
        },
        { signal }
      )
      .then(
        (
          response: AxiosResponse<{ items: CrossAggregation[] }>
        ): CrossAggregation[] => {
          return response.data.items;
        }
      );
  },
  getAttrAggr: async (
    simdataId: string,
    apiFilter: ApiDataFilter,
    aggregationType: AttributeAggregationType,
    signal?: AbortSignal
  ): Promise<CommonAggregation[]> => {
    // エージェント属性とトリップ属性で集計APIが分かれているため振り分ける
    switch (aggregationType) {
      case 'gender':
      case 'age':
      case 'nationality':
        return axiosInstance
          .post(
            `/simdata/${simdataId}/agent-attr-aggr/${aggregationType}`,
            {
              conditions: apiFilter,
            },
            { signal }
          )
          .then(
            (
              response: AxiosResponse<{ items: RawValue<CommonAggregation>[] }>
            ): CommonAggregation[] => {
              return response.data.items.map(
                (item: RawValue<CommonAggregation>): CommonAggregation => {
                  return {
                    ...item,
                    value: Number(item.value),
                  };
                }
              );
            }
          );
      case 'end_activity_type':
      case 'longest_distance_mode':
        return axiosInstance
          .post(
            `/simdata/${simdataId}/trip-attr-aggr/${aggregationType}`,
            { conditions: apiFilter },
            { signal }
          )
          .then(
            (
              response: AxiosResponse<{ items: RawValue<CommonAggregation>[] }>
            ): CommonAggregation[] => {
              return response.data.items.map(
                (item: RawValue<CommonAggregation>): CommonAggregation => {
                  return {
                    ...item,
                    value: Number(item.value),
                  };
                }
              );
            }
          );
    }
  },
  getTripHistAggr: async (
    simdataId: string,
    apiFilter: ApiDataFilter,
    aggregationType: TripHistogramType,
    bucket: Bucket,
    signal?: AbortSignal
  ): Promise<HistogramData> => {
    return axiosInstance
      .post(
        `/simdata/${simdataId}/trip-hist-aggr/${aggregationType}`,
        {
          conditions: apiFilter,
          bucket:
            bucket.type === 'BucketCount'
              ? { bucket_count: bucket.bucketCount }
              : { bucket_width: bucket.bucketWidth },
        },
        { signal }
      )
      .then((response: AxiosResponse<HistogramData | null>): HistogramData => {
        return {
          mean: response.data?.mean ?? null,
          median: response.data?.median ?? null,
          items: response.data?.items ?? [],
        };
      });
  },
  listPolygons: async (): Promise<SavedPolygons[]> => {
    return axiosInstance
      .get('/simdata/polygons')
      .then((response: AxiosResponse<SavedPolygons[]>): SavedPolygons[] => {
        return response.data;
      });
  },
  createPolygon: async (data: {
    name: string;
    type: PolygonType;
    geometry: {
      coordinates: Position[];
    };
    radius: number;
    center: Position;
    minutes: number;
  }): Promise<void> => {
    await axiosInstance.post('/simdata/polygons', data);
  },
  updatePolygon: async (
    id: string,
    changes: { name: string }
  ): Promise<void> => {
    await axiosInstance.put(`/simdata/polygons/${id}`, changes);
  },
  deletePolygon: async (id: string): Promise<void> => {
    await axiosInstance.delete(`/simdata/polygons/${id}`);
  },
  getOdMap: async (
    simdataId: string,
    mapType: OdMapType,
    showsAllOd: boolean,
    apiFilter: ApiDataFilter,
    signal?: AbortSignal
  ): Promise<OdMapResult> => {
    return axiosInstance
      .post(
        `/simdata/${simdataId}/od-map/${mapType}`,
        {
          conditions: apiFilter,
          shows_all_od: showsAllOd,
        },
        { signal }
      )
      .then((response: AxiosResponse<OdMapResult>): OdMapResult => {
        return response.data;
      });
  },
  getLinkOdMap: async (
    simdataId: string,
    mapType: OdMapType,
    showsAllOd: boolean,
    apiFilter: ApiDataFilter,
    signal?: AbortSignal
  ): Promise<OdMapResult> => {
    return axiosInstance
      .post(
        `/simdata/${simdataId}/link-od-map/${mapType}`,
        {
          conditions: apiFilter,
          shows_all_od: showsAllOd,
        },
        { signal }
      )
      .then((response: AxiosResponse<OdMapResult>): OdMapResult => {
        return response.data;
      });
  },
  downloadCsvData: async (
    simdataId: string,
    apiFilter: ApiDataFilter,
    signal?: AbortSignal
  ): Promise<string> => {
    return axiosInstance
      .post(
        `/simdata/${simdataId}/csv/download`,
        { conditions: apiFilter },
        { signal }
      )
      .then((response: AxiosResponse<string>): string => {
        return response.data;
      });
  },
};

export type {
  ApiDataFilter,
  AreaAggregationType,
  AttributeAggregationType,
  TripHistogramType,
};
export { dataApiClient, initializeDataApiClient };
